import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  DocumentChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useSettings } from '@/contexts/SettingsContext';
import { formatDateByPreference } from '@/utils/dateFormat';
import MainLayout from '../../components/layout/MainLayout';
import LoadingState from '../../components/ui/LoadingState';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: 'schedule' | 'cost' | 'resource' | 'variance';
  icon: React.ComponentType<any>;
  baselineComparison: boolean;
  estimatedTime: string;
}

interface Report {
  id: string;
  name: string;
  type: string;
  generatedAt: string;
  status: 'completed' | 'processing' | 'failed';
  downloadUrl?: string;
  baselineComparison?: boolean;
}

// Custom hook for report templates
const useReportTemplates = (projectId: string | undefined) => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Report templates API integration pending
        // const response = await fetch(`/api/projects/${projectId}/report-templates`);
        // const data = await response.json();
        // setTemplates(data);
        
        // Temporary empty state for development
        setTemplates([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load report templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [projectId]);

  return { templates, loading, error };
};

// Custom hook for recent reports
const useRecentReports = (projectId: string | undefined) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Recent reports API integration pending
        // const response = await fetch(`/api/projects/${projectId}/reports`);
        // const data = await response.json();
        // setReports(data);
        
        // Temporary empty state for development
        setReports([]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load recent reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [projectId]);

  return { reports, loading, error };
};

const ProjectReports: React.FC = () => {
  const { projectId } = useParams();
  const { settings } = useSettings();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [includeBaseline, setIncludeBaseline] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'templates' | 'recent'>('templates');

  const { templates, loading: templatesLoading, error: templatesError } = useReportTemplates(projectId);
  const { reports, loading: reportsLoading, error: reportsError } = useRecentReports(projectId);

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'schedule':
        return 'bg-nb-accent/20 text-nb-accent border-nb-accent/50';
      case 'cost':
        return 'bg-nb-success/20 text-nb-success border-nb-success/50';
      case 'resource':
        return 'bg-purple-500/20 text-purple-500 border-purple-500/50';
      case 'variance':
        return 'bg-nb-warning/20 text-nb-warning border-nb-warning/50';
      default:
        return 'bg-nb-surface-2 text-nb-text-secondary border-nb-border-subtle';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return 'bg-nb-success/20 text-nb-success border-nb-success/50';
      case 'processing':
        return 'bg-nb-warning/20 text-nb-warning border-nb-warning/50';
      case 'failed':
        return 'bg-nb-danger/20 text-nb-danger border-nb-danger/50';
      default:
        return 'bg-nb-surface-2 text-nb-text-secondary border-nb-border-subtle';
    }
  };

  const handleGenerateReport = async (): Promise<void> => {
    if (!selectedTemplate || !projectId) return;

    setIsGenerating(true);
    
    try {
      // Report generation API integration pending
      // const response = await fetch(`/api/projects/${projectId}/reports`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     templateId: selectedTemplate,
      //     includeBaseline,
      //     dateRange
      //   })
      // });
      // const result = await response.json();
      
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      console.log('Report generated:', {
        template: selectedTemplate,
        includeBaseline,
        dateRange
      });
    } catch (err) {
      console.error('Failed to generate report:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatDate = (dateString: string): string =>
    formatDateByPreference(dateString, settings.dateFormat, { showTime: true, timeZone: settings.timezone, use24Hour: settings.use24Hour });

  if (activeTab === 'templates' && templatesLoading) {
    return (
      <MainLayout title="Project Reports">
        <LoadingState message="Loading report templates..." size="lg" />
      </MainLayout>
    );
  }

  if (activeTab === 'recent' && reportsLoading) {
    return (
      <MainLayout title="Project Reports">
        <LoadingState message="Loading recent reports..." size="lg" />
      </MainLayout>
    );
  }

  if (templatesError || reportsError) {
    return (
      <MainLayout title="Project Reports">
        <div className="min-h-screen flex items-center justify-center bg-nb-bg-primary">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-nb-danger mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2 text-nb-text-primary">Error Loading Reports</h3>
            <p className="text-nb-text-muted">{templatesError || reportsError}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Project Reports">
      <div className="min-h-screen bg-nb-bg-primary">
        {/* Header */}
        <div className="shadow-sm border-b bg-nb-surface-1 border-nb-border-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-nb-text-primary">Reports</h1>
                  <p className="mt-1 text-sm text-nb-text-muted">
                    Generate and manage project reports with baseline comparisons
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setActiveTab('templates')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'templates'
                        ? 'bg-nb-accent text-white'
                        : 'bg-nb-surface-2 text-nb-text-muted hover:bg-nb-surface-3'
                    }`}
                  >
                    Report Templates
                  </button>
                  <button
                    onClick={() => setActiveTab('recent')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeTab === 'recent'
                        ? 'bg-nb-accent text-white'
                        : 'bg-nb-surface-2 text-nb-text-muted hover:bg-nb-surface-3'
                    }`}
                  >
                    Recent Reports
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'templates' ? (
          <div className="space-y-8">
            {/* Report Generation Form */}
            <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 p-6">
              <h2 className="text-lg font-medium mb-6 text-nb-text-primary">Generate New Report</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Template Selection */}
                <div>
                  <label htmlFor="report-template-select" className="block text-sm font-medium mb-3 text-nb-text-secondary">
                    Report Template
                  </label>
                  <div id="report-template-select" className="space-y-3" role="radiogroup" aria-label="Report template selection">
                    {templates.length > 0 ? (
                      templates.map((template) => (
                        <label
                          key={template.id}
                          htmlFor={`template-${template.id}`}
                          className={`block p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedTemplate === template.id
                              ? 'border-nb-accent bg-nb-accent/20'
                              : 'border-nb-border-subtle hover:border-nb-border-strong bg-nb-surface-2'
                          }`}
                        >
                          <span className="sr-only">Select {template.name} report template</span>
                          <div className="flex items-start space-x-3">
                            <input
                              id={`template-${template.id}`}
                              type="radio"
                              name="report-template"
                              value={template.id}
                              checked={selectedTemplate === template.id}
                              onChange={() => setSelectedTemplate(template.id)}
                              className="sr-only"
                              aria-label={`Select ${template.name} report template`}
                            />
                            <template.icon className="h-6 w-6 mt-0.5 text-nb-text-muted" aria-hidden="true" />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-nb-text-primary">{template.name}</h3>
                              <p className="text-sm mt-1 text-nb-text-muted">{template.description}</p>
                              <div className="flex items-center space-x-4 mt-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(template.type)}`}>
                                  {template.type}
                                </span>
                                {template.baselineComparison && (
                                  <span className="text-xs font-medium text-nb-accent">Baseline Comparison</span>
                                )}
                                <span className="text-xs text-nb-text-muted">
                                  Est. time: {template.estimatedTime}
                                </span>
                              </div>
                            </div>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="text-center py-8 text-nb-text-muted">
                        <DocumentChartBarIcon className="h-12 w-12 mx-auto mb-4 text-nb-text-muted" />
                        <p>No report templates available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Report Options */}
                <div className="space-y-6">
                  {/* Baseline Comparison */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={includeBaseline}
                        onChange={(e) => setIncludeBaseline(e.target.checked)}
                        className="rounded border border-nb-border-subtle bg-nb-surface-2 text-nb-accent focus:ring-nb-accent"
                      />
                      <span className="ml-2 text-sm text-nb-text-secondary">Include Baseline Comparison</span>
                    </label>
                    <p className="text-xs mt-1 text-nb-text-muted">
                      Compare current progress against the project baseline
                    </p>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label htmlFor="report-period" className="block text-sm font-medium mb-2 text-nb-text-secondary">
                      Report Period
                    </label>
                    <div id="report-period" className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="start-date" className="block text-xs mb-1 text-nb-text-muted">Start Date</label>
                        <input
                          id="start-date"
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                          className="block w-full px-3 py-2 border border-nb-border-subtle rounded-md bg-nb-surface-2 text-nb-text-primary focus:outline-none focus:ring-2 focus:ring-nb-accent"
                        />
                      </div>
                      <div>
                        <label htmlFor="end-date" className="block text-xs mb-1 text-nb-text-muted">End Date</label>
                        <input
                          id="end-date"
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                          className="block w-full px-3 py-2 border border-nb-border-subtle rounded-md bg-nb-surface-2 text-nb-text-primary focus:outline-none focus:ring-2 focus:ring-nb-accent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <div className="pt-4">
                    <button
                      onClick={handleGenerateReport}
                      disabled={!selectedTemplate || isGenerating || templates.length === 0}
                      className={`w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm transition-colors ${
                        !selectedTemplate || isGenerating || templates.length === 0
                          ? 'bg-nb-surface-3 text-nb-text-muted cursor-not-allowed'
                          : 'bg-nb-accent text-white hover:bg-nb-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-nb-accent'
                      }`}
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <DocumentChartBarIcon className="h-4 w-4 mr-2" />
                          Generate Report
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Types Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 p-6">
                <div className="flex items-center">
                  <ClockIcon className="h-8 w-8 text-nb-accent" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-nb-text-muted">Schedule Reports</p>
                    <p className="text-2xl font-semibold text-nb-text-primary">--</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 p-6">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-8 w-8 text-nb-success" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-nb-text-muted">Cost Reports</p>
                    <p className="text-2xl font-semibold text-nb-text-primary">--</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 p-6">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-nb-text-muted">Resource Reports</p>
                    <p className="text-2xl font-semibold text-nb-text-primary">--</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 p-6">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-8 w-8 text-nb-warning" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-nb-text-muted">Variance Reports</p>
                    <p className="text-2xl font-semibold text-nb-text-primary">--</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Recent Reports */}
            <div className="rounded-lg shadow-sm border border-nb-border-subtle bg-nb-surface-1 overflow-hidden">
              <div className="px-6 py-4 border-b border-nb-border-subtle">
                <h2 className="text-lg font-medium text-nb-text-primary">Recent Reports</h2>
              </div>
              
              <div className="divide-y divide-nb-border-subtle">
                {reports.length > 0 ? (
                  reports.map((report) => (
                    <div key={report.id} className="px-6 py-4 transition-colors hover:bg-nb-surface-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <DocumentChartBarIcon className="h-8 w-8 text-nb-text-muted" />
                          <div>
                            <h3 className="text-sm font-medium text-nb-text-primary">{report.name}</h3>
                            <div className="flex items-center space-x-3 mt-1">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTypeColor(report.type)}`}>
                                {report.type}
                              </span>
                              {report.baselineComparison && (
                                <span className="text-xs font-medium text-nb-accent">Baseline Comparison</span>
                              )}
                              <span className="text-xs text-nb-text-muted">
                                {formatDate(report.generatedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(report.status)}`}>
                            {report.status}
                          </span>
                          
                          {report.status === 'completed' && report.downloadUrl && (
                            <div className="flex items-center space-x-2">
                              <button className="p-1 transition-colors text-nb-text-muted hover:text-nb-text-primary">
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button className="p-1 transition-colors text-nb-text-muted hover:text-nb-text-primary">
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <DocumentChartBarIcon className="h-12 w-12 mx-auto mb-4 text-nb-text-muted" />
                    <h3 className="text-sm font-medium mb-2 text-nb-text-primary">No recent reports</h3>
                    <p className="text-sm text-nb-text-muted">
                      Generate your first report to see it here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </MainLayout>
  );
};

export default ProjectReports; 

